import type { AccessTokenData } from '@empathyco/playboard-core-domain'
import type { Ref } from 'vue'
import type { ExplainSectionEnum } from '../enums'
import type { PdfDetailsItem } from './use-html2pdf'
import { EngagementActionsTypeEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-actions-type.enum'
import { EngagementTypeComponentEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-type-component.enum'
import { getCurrentInstance } from 'vue'
import { mapGetters } from '../composables/utils'
import { FeatureIdEnum } from '../enums'
import { capitalizeSentence } from '../filters'
import { AUTH_CONSTS } from '../store/auth/auth.consts'
import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { useContextQuery } from './use-context-query/index'
import { useHtml2Pdf } from './use-html2pdf'
import { useTagging } from './use-tagging/index'

export function useExplainPdfDownload(): {
  creatingPdf: Ref<boolean>
  downloadExplainPdf: (explainSection: ExplainSectionEnum) => void
} {
  const ctx = getCurrentInstance()
  if (!ctx) {
    throw new Error("'ctx' is not defined")
  }

  const { taggingAction } = useTagging()
  const { query } = useContextQuery()
  const { creatingPdf, downloadPdfWithDetails } = useHtml2Pdf()

  const { context } = mapGetters(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
    context: CONTEXT_CONSTS.getters.context,
  })

  const { tokenInfo } = mapGetters<AccessTokenData>(ctx.proxy.$store, AUTH_CONSTS.moduleName, {
    tokenInfo: AUTH_CONSTS.getters.tokenInfo,
  })

  const tagPdfDownload = async (): Promise<void> => {
    await taggingAction({
      featureId: FeatureIdEnum.EXPLAIN,
      type: EngagementActionsTypeEnum.CLICK,
      component: EngagementTypeComponentEnum.DOWNLOAD,
    })
  }

  const getPdfDetails = (): Array<PdfDetailsItem> => {
    const detailsMessages = ctx.proxy.$epb.messages.explain.queryAnalysis.downloadPdf.details
    return [
      {
        label: detailsMessages.query,
        content: query.value,
        position: 'top-left',
      },
      {
        label: '',
        content: new Date().toLocaleDateString(ctx.proxy.$epb.locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZoneName: 'short',
        }),
        position: 'top-right',
      },
      {
        label: '',
        content: `${capitalizeSentence(context.value.instanceId as string)} - Confidential`,
        position: 'bottom-left',
      },
      {
        label: detailsMessages.user,
        content:
          tokenInfo.value?.user?.firstName && tokenInfo.value?.user?.lastName
            ? `${capitalizeSentence(
                tokenInfo.value.user.firstName.toLowerCase(),
              )} ${capitalizeSentence(tokenInfo.value.user.lastName.toLowerCase())}`
            : '',
        position: 'bottom-right',
      },
    ]
  }

  const modifyClonedDocument = (clonedDocument: Document): Promise<void> => {
    // Force the data-theme attribute to be empty to avoid the dark theme being applied to the PDF
    clonedDocument.documentElement.setAttribute('data-theme', '')

    const clonedElement = clonedDocument.getElementsByClassName(
      'epb-inline-tabs__content',
    )[0] as HTMLElement
    // Removes boxShadow as it is not correctly rendered by html2canvas
    clonedElement.style.boxShadow = 'none'

    // Removes the images as they cannot be properly rendered on the canvas
    const images = Array.from(clonedElement.querySelectorAll('.default-image'))
    images.forEach(image => {
      image.remove()
    })

    // Small timeout to allow the funnel to be properly rendered
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(undefined)
      }, 400)
    })
  }

  function downloadExplainPdf(explainSection: ExplainSectionEnum): void {
    downloadPdfWithDetails('epb-inline-tabs__content', {
      filename: `${query.value}-analysis-data`,
      details: getPdfDetails(),
      onDocumentClone: modifyClonedDocument,
    })
    tagPdfDownload()
  }

  return {
    creatingPdf,
    downloadExplainPdf,
  }
}
